// Router.jsx — Central routing file
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import CandidateMain from "../Frontend/Candidate/Main/CandidateMain";
import EmployerLayout from "../Frontend/Employer/Main/EmployerLayout";
import EmployerHome from "../Frontend/Employer/Employer-home/EmpHome";
import Signup from "../Frontend/Modal/SignUp/Signup";
import Loader from "../Loader/Loader";
import HraCalculator from "../Frontend/Calculator/HRA/HRA";
import PF from "../Frontend/Calculator/PF/PF";
import TaxCalculator from "../Frontend/Calculator/IncomeTax/Tax";
import PPF from "../Frontend/Calculator/PPF/PPFCalculator";
import SalaryCalculator from "../Frontend/Calculator/Salary/SalaryCalculator";
import RetirementCalculator from "../Frontend/Calculator/Retirement/RetirementCalculator";
import NPSCalculator from "../Frontend/Calculator/NPS/NPSCalculator";
import ResumeBuilder from "../Frontend/Candidate/ResumeBuilder/ResumeBuilder";
import CareerGuidance from "../Frontend/Candidate/CareerGuidence/Careerguide";
import UR from "../Frontend/Candidate/UploadResume/UR";
import SalaryGuide from "../Frontend/Calculator/SalaryGuide/SalaryGuideLive";
import EmployerSignup from "../Frontend/Modal/EmployerForm/EmployerSignup";
import Login from "../Frontend/Modal/Login/Login";
import JobsPage from "../Jobs/JobsPage";
import HeadJob from "../Frontend/Candidate/HeadJob/HeadJob";
import TalentHiveAdmin from "../Admin/TalentHiveAdmin";
import RecruiterDashboard from "../Recruiter/RecruiterDashboard";
import RecruiterProfile from "../Recruiter/RecruiterProfile";
import RewardsPage from "../Rewards/RewardsPage";
import BuyNow from "../Frontend/Employer/BuyNow/BuyNow";
import SalarySlipCalculator from "../Frontend/Calculator/SalarySlip/SalarySlipCalculator";
import CandidateProfile from "../Frontend/Candidate/Profile/CandidateProfile";
import PublicCandidateProfile from "../Frontend/Candidate/PublicProfile/PP";
import MyApplications from "../Frontend/Candidate/MyApplications/MyApplications";
import SavedJobs from "../Frontend/Candidate/SavedJobs/SavedJobs";
import HRTalk from "../Frontend/Feature/HRTalk/HRTalk";
import AdminSalaryGuide from "../Admin/AdminSalaryGuide/AdminSalaryGuide";
import CoursesPage from "../Frontend/Courses/Courses";
import ScrollToTop from "../ScrollToTop";
import HRButton from "../Frontend/Feature/HRtalkbutton/HRbutton";
import CourseMainPage from "../Frontend/Courses/CoursesPage/CoursesMainPage";
import TermsAndPolicy from "../Frontend/Main/TermsPolicy/Terms&Policy";
import AboutUs from "../Frontend/Main/About/About";
import ContactUs from "../Frontend/Main/ContactUs/Contact";
import ReportFraud from "../Frontend/Main/ReportFraud/ReportFraud";
import RelocateHub from "../Frontend/Feature/Relocate/Relocate";
import CompaniesPage from "../Companies/Companies";
import CompanyProfile from "../Companies/CompanyProfile";
import LoginPop from "../Frontend/Modal/Login/LoginPop/LoginPop";
import { useAuth } from "../../context/Authcontext";
import InterviewQuestions from "../Frontend/InterviewQuestions/Interviewquestions";
import ShoppingPage from "../Products/Shopping/Shopping";
import { SharedAccessGate } from "../Admin/AdminAccess/AdminAcccess";
import ScrollToTopBtn from "../Frontend/ScrollTo Top/ScrollToTop";

// ─── Admin Route Guard ────────────────────────────────────────────
// Checks localStorage for a valid admin token before rendering.
// If not logged in as admin → redirects to /admin/login
function AdminRoute({ children }) {
  const token = localStorage.getItem("token");

  let role = null;
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    role = user?.role;
  } catch { role = null; }

  // No token or not admin → send to login
  if (!token || role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/employee/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

const CALCULATOR_SEGMENTS = [
  "salaryslip",
  "hra-calculator",
  "pf-calculator",
  "tax-calculator",
  "ppf-calculator",
  "salary-calculator",
  "nps-calculator",
  "retirement-calculator",
];

function isCalculatorPath(pathname = "") {
  return CALCULATOR_SEGMENTS.some((segment) => pathname.includes(segment));
}

function CalculatorAuthGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const locked = !loading && !user;

  useEffect(() => {
    if (loading) return;
    if (!locked) setIsPopupOpen(false);
  }, [loading, locked, location.pathname]);

  if (loading) return null;

  return (
    <>
      <div
        style={{
          filter: locked ? "blur(1.2px)" : "none",
          pointerEvents: locked ? "none" : "auto",
          userSelect: locked ? "none" : "auto",
        }}
        aria-hidden={locked}
      >
        <Outlet />
      </div>

      {locked && !isPopupOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9990,
            background: "rgba(10, 10, 14, 0.58)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            style={{
              maxWidth: "460px",
              width: "100%",
              background: "#fff",
              borderRadius: "18px",
              padding: "22px 22px 20px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              border: "1px solid #f3f4f6",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "#f2f5ea",
                color: "#c2410c",
                padding: "7px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 700,
                marginBottom: "14px",
              }}
            >
              <span>🔒</span>
              Login required
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "22px", color: "#111827" }}>
              You need to sign in to use calculators
            </h3>
            <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.6, fontSize: "14px" }}>
              Without login, calculator tools are blocked. Sign in once and all calculator pages will unlock for free.
            </p>
            <button
              type="button"
              onClick={() => setIsPopupOpen(true)}
              style={{
                marginTop: "18px",
                width: "100%",
                height: "46px",
                border: "none",
                borderRadius: "10px",
                background: "#263012",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Open login form
            </button>
          </div>
        </div>
      )}

      {locked && isPopupOpen && (
        <LoginPop
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          onLoginSuccess={() => setIsPopupOpen(false)}
        />
      )}
    </>
  );
}

function LoginReminderPopup() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (loading) return;

    const pathname = location.pathname || "";
    const onAuthPage = pathname.includes("/login") || pathname.includes("/signup");
    const onCalculatorPage = isCalculatorPath(pathname);

    const hasSession = () => {
      try {
        const token = localStorage.getItem("th_token") || localStorage.getItem("token");
        const storedUser = localStorage.getItem("th_user") || localStorage.getItem("user");
        return Boolean(user || token || storedUser);
      } catch {
        return Boolean(user);
      }
    };

    if (onAuthPage || onCalculatorPage || hasSession()) {
      setIsOpen(false);
      return;
    }

    const showPopup = () => {
      if (!onAuthPage && !onCalculatorPage && !hasSession()) {
        setIsOpen(true);
      }
    };

    const firstTimer = window.setTimeout(showPopup, 30000);
    const repeatTimer = window.setInterval(showPopup, 30000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(repeatTimer);
    };
  }, [loading, location.pathname, user]);

  return <LoginPop isOpen={isOpen} onClose={() => setIsOpen(false)} />;
}

function getPageLabel(pathname = "") {
  const path = String(pathname || "/");
  if (path === "/") return "Home";
  if (path === "/employee") return "Employee Home";
  if (path.startsWith("/employee/login")) return "Candidate Login";
  if (path.startsWith("/employee/signup")) return "Candidate Signup";
  if (path.startsWith("/employee/profile")) return "Candidate Profile";
  if (path.startsWith("/employee/jobs")) return "Jobs";
  if (path.startsWith("/employee/courses")) return "Courses";
  if (path.startsWith("/employee/companies")) return "Companies";
  if (path.startsWith("/employee/hrtalk")) return "HRTalk";
  if (path.startsWith("/employee/recruiter-dashboard")) return "Recruiter Dashboard";
  if (path.startsWith("/employer/recruiter-dashboard")) return "Recruiter Dashboard";
  if (path.startsWith("/employer/profile")) return "Employer Profile";
  if (path.startsWith("/admin/salary-guide")) return "Admin Salary Guide";
  if (path.startsWith("/admin")) return "Admin Panel";
  if (path.startsWith("/candidate/")) return "Candidate Profile";
  if (path.includes("salaryslip")) return "Salary Slip Calculator";
  if (path.includes("hra-calculator")) return "HRA Calculator";
  if (path.includes("pf-calculator")) return "PF Calculator";
  if (path.includes("tax-calculator")) return "Tax Calculator";
  if (path.includes("ppf-calculator")) return "PPF Calculator";
  if (path.includes("salary-calculator")) return "Salary Calculator";
  if (path.includes("nps-calculator")) return "NPS Calculator";
  if (path.includes("retirement-calculator")) return "Retirement Calculator";
  if (path.includes("resume-builder")) return "Resume Builder";
  if (path.includes("ats-score-checker")) return "ATS Score Checker";
  return path.replace(/^\//, "").replace(/-/g, " ").replace(/\//g, " / ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Page";
}

function PageAnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const sessionIdRef = useRef(localStorage.getItem("th_session_id") || "");
  const activeRef = useRef({ path: location.pathname, startedAt: Date.now() });

  if (!sessionIdRef.current) {
    sessionIdRef.current = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("th_session_id", sessionIdRef.current);
  }

  const sendVisit = useCallback((path, startedAt, endedAt) => {
    if (!path) return;
    const payload = {
      session_id: sessionIdRef.current,
      page_path: path,
      page_label: getPageLabel(path),
      started_at: new Date(startedAt).toISOString(),
      ended_at: new Date(endedAt).toISOString(),
      duration_ms: Math.max(0, endedAt - startedAt),
      user_role: String(user?.role || "guest").toLowerCase(),
      user_id: user?.id || null,
      user_name: user?.name || user?.fullName || user?.username || null,
    };
    const body = JSON.stringify(payload);
    const ok = typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function";
    if (ok) {
      navigator.sendBeacon("/api/analytics/page-view", new Blob([body], { type: "application/json" }));
      return;
    }
    fetch("/api/analytics/page-view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const previous = activeRef.current;
    activeRef.current = { path: location.pathname, startedAt: Date.now() };
    return () => {
      sendVisit(previous.path, previous.startedAt, Date.now());
    };
  }, [location.pathname, sendVisit]);

  useEffect(() => {
    const onHide = () => {
      const current = activeRef.current;
      sendVisit(current.path, current.startedAt, Date.now());
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [sendVisit]);

  return null;
}

export default function Router() {
  const [loading, setLoading] = useState(true);

  return (
    <BrowserRouter>
      {/* 🐝 Loader */}
      <ScrollToTop />
      <HRButton />
      <ScrollToTopBtn/>
      {loading && (
        <Loader
          duration={3000}
          onComplete={() => setLoading(false)}
        />
      )}

      {!loading && (
        <>
          <PageAnalyticsTracker />
          <LoginReminderPopup />
          <Routes>

          {/* ─────────────────────────────────────────────────── */}
          {/*  ADMIN — standalone, no CandidateMain wrapper       */}
          {/*  Protected: requires valid admin JWT in localStorage */}
          {/* ─────────────────────────────────────────────────── */}
          {/* ─── Admin routes — TOP LEVEL ─── */}
<Route path="/admin/login" element={<Login isOpen={true} onClose={() => window.location.href = "/"} />} />
<Route
  path="/admin"
  element={
    <AdminRoute>
      <TalentHiveAdmin />
    </AdminRoute>
  }
/>
<Route
  path="/admin/salary-guide"
  element={
    <AdminRoute>
      <AdminSalaryGuide />
    </AdminRoute>
  }
/>

{/* ─── Candidate routes ─── */}
<Route path="/candidate/:username" element={<PublicCandidateProfile />} />
<Route path="/employee/public/:username" element={<PublicCandidateProfile />} />
<Route path="/employee" element={<CandidateMain />}>
  <Route index element={<HeadJob />} />
  <Route path="signup" element={<Signup />} />
  <Route path="login" element={<Login />} />
  <Route path="terms&policy" element={<TermsAndPolicy />} />
  <Route path="jobs" element={<JobsPage />} />
  <Route path="courses-main/:courseId" element={<CourseMainPage />} />
  <Route path="headjob" element={<HeadJob />} />
  <Route path="profile" element={<CandidateProfile />} />
  <Route path="applications" element={<MyApplications />} />
  <Route path="saved-jobs" element={<SavedJobs />} />
  <Route path="career-guidance" element={<CareerGuidance />} />
  <Route path="hrtalk" element={<HRTalk />} />
  <Route path="courses" element={<CoursesPage />} />
  <Route path="rewards" element={<RewardsPage />} />
  <Route path="companies" element={<CompaniesPage />} />
  <Route path="company/:companyId" element={<CompanyProfile />} /> 
  <Route path="about" element={<AboutUs />} />
  <Route path="contact" element={<ContactUs />} />
  <Route path="report-fraud" element={<ReportFraud />} />
  <Route path="relocate-hub" element={<RelocateHub />} />
  <Route path="interview-questions" element={<InterviewQuestions />} />
  <Route path="shopping" element={<ShoppingPage/>} />
     <Route path="admin/shared" element={<SharedAccessGate/>} />

  {/* Calculators */}
  <Route element={<CalculatorAuthGate />}>
    <Route path="salary-guide" element={<SalaryGuide />} />
    <Route path="ats-score-checker" element={<UR />} />
    <Route path="resume-builder" element={<ResumeBuilder />} />
    <Route path="salaryslip" element={<SalarySlipCalculator />} />
    <Route path="hra-calculator" element={<HraCalculator />} />
    <Route path="pf-calculator" element={<PF />} />
    <Route path="tax-calculator" element={<TaxCalculator />} />
    <Route path="ppf-calculator" element={<PPF />} />
    <Route path="salary-calculator" element={<SalaryCalculator />} />
    <Route path="nps-calculator" element={<NPSCalculator />} />
    <Route path="retirement-calculator" element={<RetirementCalculator />} />
  </Route>
  {/* REMOVED the duplicate /company and /company/:id with leading slashes */}
</Route>
          {/* ─────────────────────────────────────────────────── */}
          {/*  EMPLOYER ROUTES                                     */}
          {/* ─────────────────────────────────────────────────── */}
          <Route path="/employer" element={<EmployerLayout />}>
            <Route index element={<EmployerHome />} />
            <Route path="signup" element={<EmployerSignup />} />

            <Route path="recruiter-dashboard" element={<ProtectedRoute />}>
              <Route index element={<RecruiterDashboard />} />
            </Route>
            <Route path="profile" element={<ProtectedRoute />}>
              <Route index element={<RecruiterProfile />} />
            </Route>
            <Route path="rewards" element={<RewardsPage />} />
            <Route path="salary-guide" element={<SalaryGuide />} />
            <Route path="buy-now" element={<BuyNow />} />
            {/* Calculators */}
            <Route element={<CalculatorAuthGate />}>
              <Route path="hra-calculator" element={<HraCalculator />} />
              <Route path="pf-calculator" element={<PF />} />
              <Route path="tax-calculator" element={<TaxCalculator />} />
              <Route path="ppf-calculator" element={<PPF />} />
              <Route path="salary-calculator" element={<SalaryCalculator />} />
              <Route path="nps-calculator" element={<NPSCalculator />} />
              <Route path="retirement-calculator" element={<RetirementCalculator />} />
            </Route>
          </Route>

          {/* ─── Default Redirect ─────────────────────────────── */}
          <Route path="/" element={<Navigate to="/employee" replace />} />

          </Routes>
        </>
      )}
    </BrowserRouter>
  );
}
